/**
 * Rupert Neve Designs オマージュの SVG ロータリーノブ。
 *
 * - 縦ドラッグ（Shift で微調整）/ ホイール / ダブルクリックで初期値 / 矢印キー
 * - 外周にバリューリング、中央に FlowingCircles 5 色のカラーキャップ
 * - step=1 + detents + format でステップ式ロータリースイッチとしても使う
 *
 * ハードウェア風の世界観を優先するページ固有 UI のため styled-components で
 * 局所実装している（soremo-fe-rules C-3 の局所利用に該当）。
 */

import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

import { PANEL, PANEL_FONT } from "../palette";

const SWEEP_DEG = 270; // -135°..+135°
const DRAG_PIXELS_FULL_RANGE = 160;

const Root = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  user-select: none;
`;

const Label = styled.span`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: ${PANEL.engraving};
  text-transform: uppercase;
  white-space: nowrap;
`;

const KnobSvg = styled.svg`
  cursor: ns-resize;
  touch-action: none;
  outline: none;
  display: block;

  &:focus-visible {
    filter: drop-shadow(0 0 4px ${PANEL.focus});
  }
`;

const Readout = styled.span`
  min-width: 100%;
  text-align: center;
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.06em;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 3px;
  background: ${PANEL.displayBg};
  color: ${PANEL.displayText};
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.7);
  white-space: nowrap;
`;

const polar = (cx: number, cy: number, r: number, deg: number) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

/** SVG 円弧パス（時計回り、fromDeg < toDeg 前提。0° = 真上） */
const arcPath = (
  cx: number,
  cy: number,
  r: number,
  fromDeg: number,
  toDeg: number,
): string => {
  const start = polar(cx, cy, r, fromDeg);
  const end = polar(cx, cy, r, toDeg);
  const largeArc = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

export interface KnobProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  /** キャップ色（FlowingCircles の 5 色を想定） */
  color: string;
  step?: number;
  defaultValue?: number;
  /** readout の整形。未指定なら数値をそのまま表示 */
  format?: (value: number) => string;
  /** true でバリューリングを中央（12 時）起点にする（ACCEL 等） */
  bipolar?: boolean;
  size?: number;
  /** ステップ位置に打つ目盛りの数（ロータリースイッチ風の見た目用） */
  detents?: number;
}

const Knob: React.FC<KnobProps> = ({
  label,
  value,
  min,
  max,
  onChange,
  color,
  step = 1,
  defaultValue,
  format,
  bipolar = false,
  size = 64,
  detents,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  // ドラッグ中の連続値（クオンタイズ前）を保持する
  const dragValueRef = useRef(value);
  const draggingRef = useRef(false);
  const lastYRef = useRef(0);

  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const quantize = useCallback(
    (raw: number): number => {
      const clamped = Math.min(max, Math.max(min, raw));
      const snapped = Math.round((clamped - min) / step) * step + min;
      // 浮動小数の癖を除去（step が小数のときのため）
      return Number(snapped.toFixed(6));
    },
    [min, max, step],
  );

  const applyDelta = useCallback(
    (rawDelta: number) => {
      const next = Math.min(max, Math.max(min, dragValueRef.current + rawDelta));
      dragValueRef.current = next;
      const q = quantize(next);
      if (q !== valueRef.current) onChangeRef.current(q);
    },
    [min, max, quantize],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    dragValueRef.current = valueRef.current;
    lastYRef.current = e.clientY;
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!draggingRef.current) return;
      const dy = lastYRef.current - e.clientY;
      lastYRef.current = e.clientY;
      const fine = e.shiftKey ? 0.15 : 1;
      applyDelta((dy / DRAG_PIXELS_FULL_RANGE) * (max - min) * fine);
    },
    [applyDelta, max, min],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (defaultValue !== undefined) {
      dragValueRef.current = defaultValue;
      onChangeRef.current(quantize(defaultValue));
    }
  }, [defaultValue, quantize]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<SVGSVGElement>) => {
      let delta = 0;
      if (e.key === "ArrowUp" || e.key === "ArrowRight") delta = step;
      if (e.key === "ArrowDown" || e.key === "ArrowLeft") delta = -step;
      if (delta === 0) return;
      e.preventDefault();
      dragValueRef.current = valueRef.current;
      applyDelta(delta);
    },
    [applyDelta, step],
  );

  // React の onWheel は passive になり preventDefault が効かないため
  // ネイティブリスナーで登録する（ページスクロールを止めて値を回す）
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const fine = e.shiftKey ? 0.15 : 1;
      const direction = e.deltaY < 0 ? 1 : -1;
      dragValueRef.current = valueRef.current;
      applyDelta(direction * Math.max(step, (max - min) / 40) * fine);
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [applyDelta, max, min, step]);

  // ---- 描画ジオメトリ ----
  const ratio = max > min ? (value - min) / (max - min) : 0;
  const angle = -SWEEP_DEG / 2 + ratio * SWEEP_DEG;

  const ringFrom = bipolar ? Math.min(0, angle) : -SWEEP_DEG / 2;
  const ringTo = bipolar ? Math.max(0, angle) : angle;

  const pointerStart = polar(50, 50, 12, angle);
  const pointerEnd = polar(50, 50, 30, angle);

  const detentMarks: number[] = [];
  if (detents && detents >= 2) {
    for (let i = 0; i < detents; i++) {
      detentMarks.push(-SWEEP_DEG / 2 + (i / (detents - 1)) * SWEEP_DEG);
    }
  }

  const gradientId = `knob-body-${label.replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <Root $width={Math.max(size + 12, 68)}>
      <Label>{label}</Label>
      <KnobSvg
        ref={svgRef}
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="slider"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
      >
        <defs>
          <radialGradient id={gradientId} cx="38%" cy="32%" r="75%">
            <stop offset="0%" stopColor="#4a5663" />
            <stop offset="55%" stopColor="#242e39" />
            <stop offset="100%" stopColor="#141b23" />
          </radialGradient>
        </defs>

        {/* バリューリング（背景 + 現在値） */}
        <path
          d={arcPath(50, 50, 45, -SWEEP_DEG / 2, SWEEP_DEG / 2)}
          fill="none"
          stroke="rgba(35,45,56,0.18)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {ringTo - ringFrom > 0.5 && (
          <path
            d={arcPath(50, 50, 45, ringFrom, ringTo)}
            fill="none"
            stroke={color}
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}

        {/* ステップ目盛り */}
        {detentMarks.map((deg) => {
          const o = polar(50, 50, 39, deg);
          const i = polar(50, 50, 35, deg);
          return (
            <line
              key={deg}
              x1={i.x}
              y1={i.y}
              x2={o.x}
              y2={o.y}
              stroke={PANEL.tick}
              strokeWidth="1.6"
            />
          );
        })}

        {/* ノブ本体（黒スカート） */}
        <circle cx="50" cy="50" r="31" fill={`url(#${gradientId})`} stroke="#0b1016" strokeWidth="1.5" />
        {/* カラーキャップ */}
        <circle cx="50" cy="50" r="17" fill={color} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
        {/* キャップのグロス */}
        <ellipse cx="44" cy="43" rx="10" ry="7" fill="rgba(255,255,255,0.28)" />
        {/* 指針 */}
        <line
          x1={pointerStart.x}
          y1={pointerStart.y}
          x2={pointerEnd.x}
          y2={pointerEnd.y}
          stroke="#f4f7fa"
          strokeWidth="3.4"
          strokeLinecap="round"
        />
      </KnobSvg>
      <Readout>{format ? format(value) : String(value)}</Readout>
    </Root>
  );
};

export default Knob;
